import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClientSessionService } from '@brighthub/uisession';
import { ACCOUNTS_API } from './accounts-api.token';
import { mapAccountsBanner } from './accounts-banner';
import type { AccountInventoryError, AccountInventoryRow } from './accounts.model';

@Injectable()
export class AccountsStore {
  private readonly _api = inject(ACCOUNTS_API);
  private readonly _session = inject(ClientSessionService);

  private readonly _accounts = signal<AccountInventoryRow[]>([]);
  private readonly _errors = signal<AccountInventoryError[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<unknown | null>(null);
  private readonly _activeGuid = signal<string | null>(null);

  readonly accounts = this._accounts.asReadonly();
  readonly errors = this._errors.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly activeGuid = this._activeGuid.asReadonly();

  readonly banner = computed(() => mapAccountsBanner(this._errors()));

  private _generation = 0;

  readonly sessionGuid = computed(
    () => this._session.selectedSubjectData()?.guid?.trim() || null
  );

  constructor() {
    effect(() => {
      const guid = this.sessionGuid();
      if (guid) {
        untracked(() => {
          void this.load(guid);
        });
      } else {
        untracked(() => this._reset());
      }
    });
  }

  async load(guid: string): Promise<void> {
    const trimmed = guid.trim();
    if (!trimmed) {
      return;
    }
    const generation = ++this._generation;
    if (this._activeGuid() !== trimmed) {
      this._accounts.set([]);
      this._errors.set([]);
    }
    this._activeGuid.set(trimmed);
    this._loading.set(true);
    this._error.set(null);
    try {
      const payload = await firstValueFrom(this._api.getAccounts(trimmed));
      if (generation !== this._generation) {
        return;
      }
      this._accounts.set(payload.accounts ?? []);
      this._errors.set(payload.errors ?? []);
    } catch (err) {
      if (generation !== this._generation) {
        return;
      }
      console.error('[accounts] load failed');
      this._error.set(err);
      this._accounts.set([]);
      this._errors.set([]);
    } finally {
      if (generation === this._generation) {
        this._loading.set(false);
      }
    }
  }

  async reload(): Promise<void> {
    const guid = this._activeGuid() ?? this.sessionGuid();
    if (guid) {
      await this.load(guid);
    }
  }

  private _reset(): void {
    this._generation += 1;
    this._activeGuid.set(null);
    this._accounts.set([]);
    this._errors.set([]);
    this._loading.set(false);
    this._error.set(null);
  }
}
